import React from 'react';
import { Modal, Button, Select, Input, Alert, Typography } from 'antd';
import { useTranslation } from '../../hooks/useTranslation';

const { Text } = Typography;

const { TextArea } = Input;

interface JDExpressConfirmationModalProps {
  open: boolean;
  onCancel: () => void;
  onSubmit: () => void;
  onPrecheck: () => void;
  loading: boolean;
  prechecking: boolean;
  pricingCheckCompleted: boolean;
  deliveryType: 'standard' | 'express';
  deliveryAddress: string;
  deliveryNote: string;
  selectedDeliveryContactId?: string;
  deliveryContactList: Array<{ id: string; contact: string; mobile: string; address: string }>;
  onDeliveryTypeChange: (type: 'standard' | 'express') => void;
  onDeliveryAddressChange: (address: string) => void;
  onDeliveryNoteChange: (note: string) => void;
  onDeliveryContactChange: (contactId: string | undefined) => void;
}

const JDExpressConfirmationModal: React.FC<JDExpressConfirmationModalProps> = ({
  open,
  onCancel,
  onSubmit,
  onPrecheck,
  loading,
  prechecking,
  pricingCheckCompleted,
  deliveryType,
  deliveryAddress,
  deliveryNote,
  selectedDeliveryContactId,
  deliveryContactList,
  onDeliveryTypeChange,
  onDeliveryAddressChange,
  onDeliveryNoteChange,
  onDeliveryContactChange,
}) => {
  const { t } = useTranslation();

  return (
    <Modal
      title={t('appointments.jdExpress.confirmDeliveryAddress')}
      open={open}
      onOk={onSubmit}
      okButtonProps={{ loading }}
      okText={t('appointments.jdExpress.confirmCreateOrder')}
      onCancel={onCancel}
      cancelText={t('common.cancel')}
      width={600}
      footer={[
        <Button
          key="check"
          onClick={onPrecheck}
          loading={prechecking}
          style={{ marginRight: 8 }}
        >
          {t('appointments.jdExpress.checkPricingAvailability')}
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={onSubmit}
          loading={loading}
          disabled={!pricingCheckCompleted}
          title={!pricingCheckCompleted ? t('appointments.jdExpress.pleaseCheckPricingFirst') : ""}
        >
          {t('appointments.jdExpress.confirmCreateOrder')}
        </Button>,
      ]}
    >
      <div style={{ marginBottom: 16 }}>
        <Text type="secondary">
          {t('appointments.jdExpress.deliveryAddressDescription')}
        </Text>
        {pricingCheckCompleted && (
          <div style={{ marginTop: 8 }}>
            <Alert
              message={t('appointments.jdExpress.pricingCheckCompleted')}
              type="success"
              showIcon
              style={{ fontSize: '12px' }}
            />
          </div>
        )}
      </div>
      
      <div style={{ marginBottom: 16 }}>
        <div style={{ marginBottom: 8 }}>
          <Text strong>{t('appointments.jdExpress.deliveryType')}</Text>
        </div>
        <Select
          style={{ width: '100%' }}
          value={deliveryType}
          onChange={(value) => onDeliveryTypeChange(value)}
          options={[
            { value: 'standard', label: t('appointments.jdExpress.standardDelivery') },
            { value: 'express', label: t('appointments.jdExpress.expressDelivery') }
          ]}
        />
      </div>
      
      <div style={{ marginBottom: 16 }}>
        <div style={{ marginBottom: 8 }}>
          <Text strong>{t('appointments.jdExpress.selectDeliveryContact') || 'Select Delivery Contact (Optional)'}</Text>
        </div>
        <Select
          style={{ width: '100%' }}
          placeholder={t('appointments.jdExpress.selectDeliveryContactPlaceholder') || 'Select a delivery contact...'}
          value={selectedDeliveryContactId}
          onChange={(value) => {
            onDeliveryContactChange(value);
          }}
          options={deliveryContactList.map((contact) => ({
            value: contact.id,
            label: `${contact.address} (${contact.contact})`
          }))}
        />
        <div style={{ marginTop: 8 }}>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {t('appointments.jdExpress.selectDeliveryContactNote') || 'Select a contact to auto-fill the address, or enter manually below.'}
          </Text>
        </div>
      </div>
      
      <div style={{ marginBottom: 8 }}>
        <Text strong>{t('appointments.jdExpress.deliveryAddress')}</Text>
      </div>
      <TextArea
        rows={4}
        value={deliveryAddress}
        onChange={(e) => onDeliveryAddressChange(e.target.value)}
        placeholder={t('appointments.jdExpress.deliveryAddressPlaceholder')}
      />
      <div style={{ marginTop: 8 }}>
        <Text type="secondary" style={{ fontSize: '12px' }}>
          {t('appointments.jdExpress.deliveryAddressNote')}
        </Text>
      </div>
      
      <div style={{ marginTop: 16, marginBottom: 8 }}>
        <Text strong>{t('appointments.jdExpress.deliveryNote')}</Text>
      </div>
      <TextArea
        rows={3}
        value={deliveryNote}
        onChange={(e) => onDeliveryNoteChange(e.target.value)}
        placeholder={t('appointments.jdExpress.deliveryNotePlaceholder')}
      />
      <div style={{ marginTop: 8 }}>
        <Text type="secondary" style={{ fontSize: '12px' }}>
          {t('appointments.jdExpress.deliveryNoteDescription')}
        </Text>
      </div>
    </Modal>
  );
};

export default JDExpressConfirmationModal;
